import React, { Suspense, useContext } from 'react';
import { Navigate, Route, Routes, useSearchParams } from 'react-router-dom';
import AuthContext from '../../context/AuthProvider';
import { usePermission } from '../../hooks/usePermission';
import EmailRedirectView from '../../views/EmailRedirectView';
import ProtectedRoute from './ProtectedRoute';
import { PanelVerificationProvider } from '../verification/PanelVerificationProvider';

// components
const Menu = React.lazy(() => import('../menu/Menu'));
const PrimeStatusLayout = React.lazy(() =>
  import('../prime/PrimeStatusLayout'),
);
const RequestsView = React.lazy(() => import('../../views/RequestsView'));
const LoginView = React.lazy(() => import('../../views/LoginView'));
const RegisterView = React.lazy(() => import('../../views/RegisterView'));
const RecoveryPasswordView = React.lazy(() =>
  import('../../views/RecoveryPasswordView'),
);
const ActivateAccountView = React.lazy(() =>
  import('../../views/ActivateAccountView'),
);
const SelectPanelView = React.lazy(() => import('../../views/SelectPanelView'));
const Home = React.lazy(() => import('../../views/HomeView'));
const Users = React.lazy(() => import('../users/Users'));
const CreateTripView = React.lazy(() => import('../../views/CreateTripView'));
const ViewOnMap = React.lazy(() => import('../../views/ViewOnMap'));
const Development = React.lazy(() => import('../../views/DevelopmentView'));
const Payments = React.lazy(() => import('../payments/Payments'));
const UserDetail = React.lazy(() => import('../../views/UserDetail'));
const PrimeView = React.lazy(() => import('../../views/PrimeView'));
const SettingsView = React.lazy(() => import('../../views/SettingsView'));

// ruta PublicSharp, soilo se puede acceder al login
// public Route, only login is allowed
export const RouterPublic = () => {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Routes>
        <Route path="/login" exact element={<LoginView />} />
        <Route path="/register" exact element={<RegisterView />} />
        <Route path="/recovery-pswd" exact element={<RecoveryPasswordView />} />
        {/* Segundo paso del login multi-panel: el usuario se autenticó pero
            todavía no tiene sesión, así que la pantalla es pública. */}
        <Route path="/select-panel" exact element={<SelectPanelView />} />
        <Route
          path="/activate-account"
          exact
          element={<ActivateAccountView />}
        />
        <Route path="/email-us" element={<EmailRedirectView />} />
        <Route path="/" element={<Navigate replace to="login" exact />} />
        <Route path="*" element={<Navigate replace to="login" exact />} />
      </Routes>
    </Suspense>
  );
};

// ruta privada, solo disponible si el usuario esta autenticado
// private route, only allowed if the user is authenticated
export const RouterPrivate = () => {
  const { can } = usePermission();
  const { datalogin } = useContext(AuthContext);
  const [searchParams] = useSearchParams();

  // The shipment variant is a query param, not a route segment, so the guard is
  // read here instead of inside `CreateTripView`: that view is `React.lazy`, and
  // a guard in there would decide after the chunk loaded and the flow mounted.
  //
  // The predicate has to mirror the renderer, which is `variant === 'ride'`
  // (`CreateTrip.jsx`): anything else mounts the shipment flow, so an unknown
  // variant like `?type=foo` has to be gated too. Checking for `'shipment'`
  // alone would leave that bypass open.
  const isRideVariant = (searchParams.get('type') || 'ride') === 'ride';

  return (
    <Suspense fallback={<div>Loading...</div>}>
      {/* Verificación del panel (CORP-599): chip del header, modal y aviso
          ante un 1185. Envuelve el banner Prime, el menú y las rutas. */}
      <PanelVerificationProvider>
        {can('financial:read') && (
          <div className="sticky top-0 right-0 left-0 z-50">
            <PrimeStatusLayout />
          </div>
        )}
        <Menu />
        <Routes>
          <Route path="/home" exact element={<Home />} />
          <Route
            path="/trips"
            exact
            element={
              <ProtectedRoute requiredPermission="logistics:read">
                <RequestsView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            exact
            element={
              <ProtectedRoute requiredPermission="collaborators:read">
                <Users />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/detail"
            exact
            element={
              <ProtectedRoute requiredPermission="collaborators:read">
                <UserDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/create-trip"
            element={
              <ProtectedRoute
                requiredPermission="logistics:read"
                isEnabled={
                  isRideVariant || datalogin?.hasShipmentModuleActive === true
                }
              >
                <CreateTripView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/map-view"
            exact
            element={
              <ProtectedRoute requiredPermission="logistics:read">
                <ViewOnMap />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payments"
            exact
            element={
              <ProtectedRoute requiredPermission="financial:read">
                <Payments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/prime"
            exact
            element={
              <ProtectedRoute requiredPermission="financial:read">
                <PrimeView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/development"
            exact
            element={
              <ProtectedRoute
                requiredPermission="development:read"
                // Child corporates never get the module. `enable_dev_module` no
                // longer gates the route: the view itself shows the activation
                // request while it is off, without calling the dev endpoints.
                isEnabled={datalogin?.is_child_corporate !== true}
              >
                <Development />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            exact
            element={
              <ProtectedRoute requiredPermission="all:all">
                <SettingsView />
              </ProtectedRoute>
            }
          />
          <Route path="/email-us" element={<EmailRedirectView />} />
          <Route path="/" exact element={<Navigate replace to="home" />} />
          <Route path="*" element={<Navigate replace to="/home" />} />
        </Routes>
      </PanelVerificationProvider>
    </Suspense>
  );
};
